// FILE: src/utils/menuCsvParser.js

/**
 * ManiDine CSV menu import helper.
 *
 * Required CSV columns:
 * name,description,category,price,isAvailable
 *
 * Optional CSV columns:
 * taxRate,status,sellOnItsOwn,itemType
 *
 * Important:
 * This file only parses and validates CSV data.
 * It does not save anything to Firestore.
 */

export const MENU_CSV_TEMPLATE = `name,description,category,price,isAvailable,taxRate,status,sellOnItsOwn,itemType
Butter Chicken,"Creamy tomato chicken curry",Curries,16.99,yes,5,available,yes,single
Garlic Naan,"Naan with garlic butter",Breads,3.99,yes,5,available,yes,single
Mango Lassi,"Yogurt mango drink",Drinks,5.99,yes,5,available,yes,single
`;

export const MENU_CSV_REQUIRED_HEADERS = [
  "name",
  "description",
  "category",
  "price",
  "isavailable",
];

export const MENU_CSV_OPTIONAL_HEADERS = [
  "taxrate",
  "status",
  "sellonitsown",
  "itemtype",
];

const normalizeHeader = (value) =>
  (value || "")
    .toString()
    .trim()
    .replace(/\s+/g, "")
    .replace(/[_-]/g, "")
    .toLowerCase();

const cleanCell = (value) => (value ?? "").toString().trim();

const parseBoolean = (value, fallback = true) => {
  const normalized = cleanCell(value).toLowerCase();

  if (!normalized) return fallback;

  if (["yes", "y", "true", "available", "1", "on"].includes(normalized)) {
    return true;
  }

  if (["no", "n", "false", "unavailable", "out", "0", "off"].includes(normalized)) {
    return false;
  }

  return fallback;
};

const normalizeStatus = (statusValue, isAvailable) => {
  const normalized = cleanCell(statusValue).toLowerCase();

  if (["archived", "archive"].includes(normalized)) return "archived";
  if (["out", "out of stock", "unavailable", "inactive"].includes(normalized)) {
    return "out";
  }
  if (["available", "active", "in stock"].includes(normalized)) {
    return "available";
  }

  return isAvailable ? "available" : "out";
};

const normalizeItemType = (value) => {
  const normalized = cleanCell(value).toLowerCase();
  return normalized === "combo" ? "combo" : "single";
};

/**
 * Parse CSV safely, including quoted values containing commas.
 * Example:
 * Butter Chicken,"Creamy, rich tomato curry",Curries,16.99,yes
 */
export function parseCsvRows(csvText) {
  const rows = [];
  let currentRow = [];
  let currentCell = "";
  let insideQuotes = false;

  for (let i = 0; i < csvText.length; i += 1) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];

    if (char === '"' && insideQuotes && nextChar === '"') {
      currentCell += '"';
      i += 1;
      continue;
    }

    if (char === '"') {
      insideQuotes = !insideQuotes;
      continue;
    }

    if (char === "," && !insideQuotes) {
      currentRow.push(currentCell);
      currentCell = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !insideQuotes) {
      if (char === "\r" && nextChar === "\n") {
        i += 1;
      }

      currentRow.push(currentCell);

      const hasContent = currentRow.some((cell) => cleanCell(cell).length > 0);
      if (hasContent) rows.push(currentRow);

      currentRow = [];
      currentCell = "";
      continue;
    }

    currentCell += char;
  }

  currentRow.push(currentCell);
  const hasContent = currentRow.some((cell) => cleanCell(cell).length > 0);
  if (hasContent) rows.push(currentRow);

  return rows;
}

/**
 * Main parser used by MenuManager.jsx.
 */
export function parseMenuCsv(csvText) {
  if (!csvText || typeof csvText !== "string") {
    return {
      success: false,
      errors: ["CSV file is empty."],
      warnings: [],
      items: [],
      summary: {
        totalRows: 0,
        validItems: 0,
        categories: [],
      },
    };
  }

  const rows = parseCsvRows(csvText);

  if (rows.length < 2) {
    return {
      success: false,
      errors: ["CSV contains no menu items."],
      warnings: [],
      items: [],
      summary: {
        totalRows: rows.length,
        validItems: 0,
        categories: [],
      },
    };
  }

  const rawHeaders = rows[0].map((header) => cleanCell(header));
  const headers = rawHeaders.map(normalizeHeader);

  const duplicateHeaders = headers.filter(
    (header, index) => header && headers.indexOf(header) !== index
  );

  const missingHeaders = MENU_CSV_REQUIRED_HEADERS.filter(
    (header) => !headers.includes(header)
  );

  if (missingHeaders.length > 0) {
    return {
      success: false,
      errors: [`Missing required columns: ${missingHeaders.join(", ")}`],
      warnings: duplicateHeaders.length
        ? [`Duplicate column names found: ${[...new Set(duplicateHeaders)].join(", ")}`]
        : [],
      items: [],
      summary: {
        totalRows: rows.length - 1,
        validItems: 0,
        categories: [],
      },
    };
  }

  const items = [];
  const errors = [];
  const warnings = [];
  const seenNamesInCategory = new Set();

  if (duplicateHeaders.length > 0) {
    warnings.push(
      `Duplicate column names found: ${[...new Set(duplicateHeaders)].join(", ")}`
    );
  }

  rows.slice(1).forEach((values, index) => {
    const rowNumber = index + 2;
    const row = {};

    headers.forEach((header, headerIndex) => {
      if (!header) return;
      row[header] = cleanCell(values[headerIndex]);
    });

    const name = cleanCell(row.name);
    const description = cleanCell(row.description);
    const category = cleanCell(row.category);
    const priceText = cleanCell(row.price).replace(/[$,]/g, "");
    const price = Number.parseFloat(priceText);

    if (!name) {
      errors.push(`Row ${rowNumber}: Item name is required.`);
      return;
    }

    if (!category) {
      errors.push(`Row ${rowNumber}: Category is required.`);
      return;
    }

    if (Number.isNaN(price) || price < 0) {
      errors.push(`Row ${rowNumber}: Invalid price.`);
      return;
    }

    const isAvailable = parseBoolean(row.isavailable, true);
    const status = normalizeStatus(row.status, isAvailable);
    const sellOnItsOwn = parseBoolean(row.sellonitsown, true);
    const itemType = normalizeItemType(row.itemtype);

    const taxRateText = cleanCell(row.taxrate);
    const taxRate =
      taxRateText === ""
        ? null
        : Number.isNaN(Number.parseFloat(taxRateText))
        ? null
        : Number.parseFloat(taxRateText);

    const duplicateKey = `${category.toLowerCase()}::${name.toLowerCase()}`;
    if (seenNamesInCategory.has(duplicateKey)) {
      warnings.push(
        `Row ${rowNumber}: Possible duplicate item "${name}" in category "${category}".`
      );
    }
    seenNamesInCategory.add(duplicateKey);

    items.push({
      rowNumber,
      name,
      description,
      category,
      categoryId: "",
      price,
      taxRate,
      status,
      sellOnItsOwn,
      isAvailable: status === "available" && isAvailable,
      itemType,
      comboItems: [],
      comboGroups: [],
      comboMode: itemType === "combo" ? "groups" : "single",
    });
  });

  const categories = [...new Set(items.map((item) => item.category).filter(Boolean))].sort(
    (a, b) => a.localeCompare(b)
  );

  return {
    success: errors.length === 0,
    errors,
    warnings,
    items,
    summary: {
      totalRows: rows.length - 1,
      validItems: items.length,
      categories,
    },
  };
}